<script setup lang="ts">
import type {ArticleAsset} from '@/types';

const props = defineProps({
  article: {
    type: Object as () => ArticleAsset,
    required: true,
  },
});

const {translate} = useTranslation();

const localePath = useLocalePath();

const getBackground = computed(() => {
  if (data.value) {
    return {
      'background-image': `linear-gradient(180deg, rgba(0, 0, 0, 0) 44%, rgba(0, 0, 0, 0.66) 84.57%), url(${data.value.fields.thumbnail_image.public_url})`,
    };
  } else {
    return null;
  }
});

const data = ref<ArticleAsset>();

if (import.meta.client) {
  const {data: lazyResp, error} = await useLazyFetch<ArticleAsset>(`/api/asset-by-slug?slug=${props.article.slug}&status=${window ? (window.location !== window.parent.location ? 'draft' : 'published') : 'published'}`, {server: false});

  if (lazyResp.value && !error) {
    data.value = lazyResp.value as ArticleAsset;
  }

  watch(lazyResp, (article: ArticleAsset) => {
    data.value = article;
  });
}
</script>

<template>
  <li v-if="data" class="feature-item bg-black relative overflow-hidden pb-[100%]">
    <TheNuxtLink :to="data.fields.external_url ? data.fields.external_url.value : localePath(`/article/${data.slug}`)" :target="data.fields.external_url && data.fields.external_url.value.includes('http') ? '_blank' : '_self'" :aria-label="`Read more - ${data.fields.thumbnail_title.value}`" class="block">
      <div :style="getBackground" class="background-image absolute top-0 left-0 h-full w-full" />

      <div class="feature-item__content">
        <h3 class="text-white text-left font-extrabold max-sm:text-shadow-lg max-sm:m-height-auto">
          {{ translate(data.fields.thumbnail_title) }}
        </h3>
        <p v-if="data.fields.thumbnail_subtitle" class="feature-blurb text-white font-normal leading-12">
          {{ translate(data.fields.thumbnail_subtitle) }}
        </p>
      </div>
    </TheNuxtLink>
  </li>
</template>

<style lang="scss" scoped>
@use '@/assets/scss/queries';
li.feature-item {
  position: relative;
  a {
    .background-image {
      background-position: center;
      background-repeat: no-repeat;
      background-size: 150%;
      transition: background-size 0.25s;
    }

    .feature-item__content {
      bottom: 0;
      display: flex;
      flex-direction: column;
      gap: 3rem;
      padding: 6rem 3rem;
      position: absolute;

      @include queries.for-size(desktop-lg) {
        padding: 3rem;
      }

      @include queries.for-size(device) {
        padding: 1rem 1rem 0 1rem;
      }

      @include queries.for-size(phone) {
        padding: 2rem;
        bottom: 0;
      }

      h3 {
        line-height: 0.85;
        width: 70%;
        font-size: 3.75rem;

        @include queries.for-size(desktop-lg) {
          font-size: 3rem;
        }

        @include queries.for-size(device) {
          padding: 0 1.5rem 0 0;
        }

        @include queries.for-size(phone) {
          font-size: 2.2rem !important;
          line-height: 0.9;
        }
      }

      p {
        font-size: clamp(1.25rem, 3.5cqw, 2.5rem);
        line-height: 1.2;
        max-height: 0;
        overflow: hidden;
        transition: all 0.5s ease-out;

        @include queries.for-size(desktop-lg) {
          font-size: 1.5rem;
        }

        @include queries.for-size(phone) {
          display: none;
        }
      }
    }

    &:hover {
      .background-image {
        background-size: 170%;
      }

      .feature-item__content {
        p {
          max-height: 200px;
        }
      }
    }
  }
}
</style>
